import { computed, ref } from "vue";
import type { ThemeColorMode, ThemePublicDto, ThemeSettings, UserAppearanceDto } from "../types/theme";
import { buildThemeCss } from "../services/theme/themeCss";
import { loadThemeFont } from "../services/theme/themeFonts";
import { themeTL } from "../generated/tl";

/**
 * Vzhled aplikace (docs/tasks/design/plan-001.md). Aktivní téma vloží server do HTML jako
 * `window.__fxTheme` (HomePresenter), takže `initTheme()` ho aplikuje synchronně před vykreslením.
 * Barevný režim: volba uživatele (localStorage) > výchozí režim tématu; "system" sleduje OS.
 */

declare global {
	interface Window { __fxTheme?: ThemePublicDto; __fxAppearance?: UserAppearanceDto | null }
}

const STORAGE_KEY = 'fx-color-mode';
const STYLE_ID = 'fx-theme';

const activeTheme = ref<ThemePublicDto | null>(null);
// co se právě zobrazuje: aktivní téma, nebo rozpracovaný náhled z administrace
const shownTheme = ref<ThemePublicDto | null>(null);
const userMode = ref<ThemeColorMode | null>(readStoredMode());
const systemDark = ref(false);
// Osobní přizpůsobení vzhledu (docs/tasks/063-vzhled-2.md): přepisuje vzhled z administrace.
const emptyAppearance = (): UserAppearanceDto => ({ themeId: null, density: null, fontScale: null, colorMode: null });
const appearance = ref<UserAppearanceDto>(emptyAppearance());
// poslední uložená podoba (k ní se vrací zavření dialogu bez uložení) + režim, který platil před úpravami
let savedAppearance: UserAppearanceDto = emptyAppearance();
let appearanceKnown = false; // false = nepřihlášený uživatel, nic se neukládá do DB
// administrace právě zobrazuje rozpracovaný náhled tématu → osobní přepisy se nepoužijí
let previewing = false;

function readStoredMode(): ThemeColorMode | null {
	try {
		const v = localStorage.getItem(STORAGE_KEY);
		return v === 'light' || v === 'dark' || v === 'system' ? v : null;
	} catch {
		return null;
	}
}

const effectiveDark = computed(() => {
	const mode = userMode.value ?? shownTheme.value?.settings.defaultColorMode ?? 'light';
	return mode === 'dark' || (mode === 'system' && systemDark.value);
});

const applyToDom = () => {
	const theme = shownTheme.value;
	if (!theme) return;
	let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
	if (!style) {
		style = document.createElement('style');
		style.id = STYLE_ID;
		document.head.appendChild(style);
	}
	const overrides = previewing ? {} : { density: appearance.value.density, fontScale: appearance.value.fontScale };
	style.textContent = buildThemeCss(theme.settings, { light: theme.logoLightUrl, dark: theme.logoDarkUrl }, overrides);
	const root = document.documentElement;
	root.setAttribute('data-bs-theme', effectiveDark.value ? 'dark' : 'light');
	// textura pozadí se v tmavém režimu nepoužívá (je navržená pro světlé pozadí)
	root.setAttribute('data-fx-pattern', theme.settings.backgroundPattern && !effectiveDark.value ? 'on' : 'off');
	void loadThemeFont(theme.settings.fonts.body);
	void loadThemeFont(theme.settings.fonts.heading);
};

const show = (theme: ThemePublicDto) => {
	shownTheme.value = theme;
	applyToDom();
};

let initialized = false;

/** Zavolat jednou v main.ts před vytvořením aplikace. */
export const initTheme = (): void => {
	if (initialized) return;
	initialized = true;
	const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
	systemDark.value = !!mq?.matches;
	mq?.addEventListener?.('change', e => { systemDark.value = e.matches; applyToDom(); });
	if (window.__fxAppearance) adoptAppearance(window.__fxAppearance);
	if (window.__fxTheme) {
		activeTheme.value = window.__fxTheme;
		show(window.__fxTheme);
	}
};

/** Převezme uložené osobní nastavení (z HTML nebo ze serveru) — bez překreslení, to udělá volající. */
const adoptAppearance = (dto: UserAppearanceDto) => {
	appearance.value = { ...dto };
	savedAppearance = { ...dto };
	appearanceKnown = true;
	if (dto.colorMode) storeMode(dto.colorMode);
};

const storeMode = (mode: ThemeColorMode | null) => {
	userMode.value = mode;
	try {
		if (mode === null) localStorage.removeItem(STORAGE_KEY);
		else localStorage.setItem(STORAGE_KEY, mode);
	} catch { /* soukromé okno apod. — volba platí jen do reloadu */ }
};

export function useTheme() {
	const setColorMode = (mode: ThemeColorMode | null) => {
		storeMode(mode);
		applyToDom();
	};

	/** Přepíná světlý ↔ tmavý podle toho, co se právě zobrazuje; přihlášenému uživateli volbu uloží. */
	const toggleDark = () => {
		const mode: ThemeColorMode = effectiveDark.value ? 'light' : 'dark';
		setColorMode(mode);
		if (!appearanceKnown) return;
		appearance.value = { ...appearance.value, colorMode: mode };
		savedAppearance = { ...savedAppearance, colorMode: mode };
		void themeTL.saveMyAppearance(savedAppearance).catch(() => { /* zůstane aspoň v localStorage */ });
	};

	/** Znovu načte vzhled ze serveru (po změně v administraci) — s osobním výběrem tématu. */
	const refreshActiveTheme = async () => {
		const theme = await themeTL.getMyTheme();
		activeTheme.value = theme;
		show(theme);
	};

	/** Načte osobní nastavení přihlášeného uživatele (když nepřišlo v HTML) a aplikuje ho. */
	const loadAppearance = async () => {
		if (appearanceKnown) return;
		adoptAppearance(await themeTL.getMyAppearance());
		await refreshActiveTheme();
	};

	/** Zapomene osobní nastavení po odhlášení. */
	const clearAppearance = () => {
		appearance.value = emptyAppearance();
		savedAppearance = emptyAppearance();
		appearanceKnown = false;
	};

	// Úpravy v dialogu: každá změna se hned projeví (náhled), uložení a návrat řeší následující funkce.
	let modeBeforeEdit: ThemeColorMode | null = null;
	const beginAppearanceEdit = () => { modeBeforeEdit = userMode.value; };

	/** Okamžitě aplikuje (náhled) změnu osobního nastavení; `theme` = vybrané téma (null = výchozí). */
	const previewAppearance = (patch: Partial<UserAppearanceDto>, theme?: ThemePublicDto | null) => {
		appearance.value = { ...appearance.value, ...patch };
		if (patch.colorMode !== undefined) storeMode(patch.colorMode);
		if (theme) show(theme);
		else applyToDom();
	};

	const saveAppearance = async () => {
		const result = await themeTL.saveMyAppearance(appearance.value);
		if (result.status === 'error') return result;
		savedAppearance = { ...result.data };
		appearance.value = { ...result.data };
		appearanceKnown = true;
		return result;
	};

	/** Zahodí neuložené změny — vrátí uložené nastavení, téma i režim. */
	const revertAppearance = async () => {
		appearance.value = { ...savedAppearance };
		storeMode(savedAppearance.colorMode ?? modeBeforeEdit);
		await refreshActiveTheme();
	};

	/** Okamžitý náhled rozpracovaného nastavení (administrace). */
	const preview = (settings: ThemeSettings, logos: { light: string | null, dark: string | null }) => {
		const base = activeTheme.value;
		previewing = true;
		show({
			id: base?.id ?? null, name: base?.name ?? '', isActive: false, userSelectable: false,
			settings, logoLightUrl: logos.light, logoDarkUrl: logos.dark,
		});
	};
	const endPreview = () => {
		previewing = false;
		if (activeTheme.value) show(activeTheme.value);
	};

	const logoUrl = computed(() => {
		const t = shownTheme.value;
		if (!t) return null;
		return effectiveDark.value ? (t.logoDarkUrl ?? t.logoLightUrl) : (t.logoLightUrl ?? t.logoDarkUrl);
	});

	/** Horní lišta je tmavá v obou režimech → preferuje se logo pro tmavé pozadí. */
	const navbarLogoUrl = computed(() => {
		const t = shownTheme.value;
		return t ? (t.logoDarkUrl ?? t.logoLightUrl) : null;
	});

	return {
		activeTheme, shownTheme, isDark: effectiveDark, userMode, logoUrl, navbarLogoUrl, appearance,
		setColorMode, toggleDark, refreshActiveTheme, preview, endPreview,
		loadAppearance, clearAppearance, beginAppearanceEdit, previewAppearance, saveAppearance, revertAppearance,
	};
}
